import { useIDCardQuery } from "@/hooks/use-id-card";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { cn, downloadBase64File } from "@/lib/utils";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { useFeatureName } from "@/hooks/use-pageTitle";
import { getPlanCoverageTypeName } from "@/lib/plan-coverage-utils";
import { Download } from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { LogSubCategories } from "@/services/logger/logger-types";
import IDENTIFIERS from "@/constants/test_id";

export const DownloadId = () => {
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const { addError } = useNotificationError();
  const featureName = useFeatureName();
  const {
    data: idCardData,
    isFetching,
    isError,
    error,
  } = useIDCardQuery(activeCoverage);

  const initiateDownload = () => {
    if (isFetching || isError || error) {
      addError({
        message: "Failed to setup Id Card Download.",
        type: ErrorNotificationType.ERROR,
      });
      return;
    }

    const success = downloadBase64File(
      idCardData?.base64Encoded ?? "",
      `${featureName}_${getPlanCoverageTypeName(activeCoverage?.claimCoverageCode)}.pdf`,
    );

    if (!success) {
      addError({
        message: "Id Card downloaded failed.",
        type: ErrorNotificationType.ERROR,
      });
    }
  };

  return (
    /*
      2.1.1 Keyboard — A. This was a <span onClick={initiateDownload}> wrapping the real
      <Button>, i.e. the download only ran because the inner button's click bubbled up to
      the span. That happens to work for keyboard users, but it leaves the actual download
      on a non-interactive element — and any change to event handling silently breaks it.
      The handler now lives on the button that performs the action.
    */
    <DownloadIcon onDownload={initiateDownload} />
  );
};

const DownloadIcon = ({ onDownload }: { onDownload: () => void }) => {
  const [animate, setAnimate] = useState(false);

  const handleClick = () => {
    setAnimate(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimate(true));
    });
    onDownload();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          // 4.1.2 Name, Role, Value — A: icon-only. The tooltip is not a reliable
          // accessible name — it is unavailable to touch users and skipped by some
          // screen readers — so the name is set explicitly.
          aria-label="Download ID card"
          onClick={handleClick}
          className={cn(
            "inline-flex items-center justify-center gap-0 rounded-md bg-transparent p-3 text-black shadow-none hover:rounded-full hover:bg-primary-extraLight",
            "size-fit cursor-pointer transition-all duration-150",
          )}
          logSubCategory={LogSubCategories.DOWNLOAD_ID_CARD}
          data-testid={IDENTIFIERS.ID_CARD.DOWNLOAD_ID_CARD_BUTTON}
        >
          <Download
            size={20}
            className={animate ? "animate-bounce-download" : ""}
            onAnimationEnd={() => setAnimate(false)}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Download Id Card</p>
      </TooltipContent>
    </Tooltip>
  );
};
